// @ts-nocheck
import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useMyOrganizations } from "@/contexts/MyOrganizationsContext";

function roleLabel(role: string | null): string {
  if (!role) return "Member";
  const map: Record<string, string> = {
    org_admin: "Org Admin",
    rlevents_tenant_admin: "Org Admin",
    rlevents_tenant_staff: "Staff",
    admin: "Admin",
    owner: "Owner",
    member: "Member",
  };
  return map[role] || role.replace(/_/g, " ");
}

function OrgAvatar({ org, className }: { org: any; className?: string }) {
  const [failed, setFailed] = React.useState(false);
  const showLogo = org?.logo_url && !failed;
  if (showLogo) {
    return (
      <img
        src={org.logo_url}
        alt={`${org.name} logo`}
        className={cn("h-8 w-8 shrink-0 rounded-lg object-contain", className)}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sm font-bold text-sidebar-primary-foreground",
        className
      )}
    >
      {(org?.name || "O").charAt(0).toUpperCase()}
    </span>
  );
}

export function OrgSwitcher() {
  const ctx = useMyOrganizations();
  const navigate = useNavigate();

  const active = ctx?.activeOrganization;
  const orgs = ctx?.organizations ?? [];
  const orgName = active?.name || "Organization";
  const multi = orgs.length > 1;

  const trigger = (
    <button
      type="button"
      className="flex w-full items-center gap-2 overflow-hidden rounded-lg px-1 py-1 text-left hover:bg-sidebar-accent group-data-[collapsible=icon]:justify-center"
    >
      <OrgAvatar org={active} />
      <span className="min-w-0 flex-1 truncate text-lg font-bold tracking-tight text-sidebar-foreground group-data-[collapsible=icon]:hidden">
        {orgName}
      </span>
      {multi && (
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
      )}
    </button>
  );

  // Single org: no dropdown needed, just show it (clicking goes home).
  if (!multi) {
    return (
      <button
        type="button"
        onClick={() => navigate("/admin-dashboard")}
        className="flex w-full items-center gap-2 overflow-hidden rounded-lg px-1 py-1 text-left hover:bg-sidebar-accent group-data-[collapsible=icon]:justify-center"
      >
        <OrgAvatar org={active} />
        <span className="min-w-0 flex-1 truncate text-lg font-bold tracking-tight text-sidebar-foreground group-data-[collapsible=icon]:hidden">
          {orgName}
        </span>
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Your organizations</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {orgs.map((org) => {
          const isActive = org.id === ctx?.activeOrgId;
          return (
            <DropdownMenuItem
              key={org.id}
              className="gap-2"
              onSelect={() => ctx?.setActiveOrgId(org.id)}
            >
              <OrgAvatar org={org} className="h-7 w-7" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{org.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {roleLabel(org.role)}
                </div>
              </div>
              {isActive && <Check className="h-4 w-4 shrink-0 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { roleLabel, OrgAvatar };
export default OrgSwitcher;
